import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, CheckCheck } from 'lucide-react';

const messages = [
  {
    text: "⚽ Your prediction for Arsenal vs Chelsea (2-1) has been saved! Good luck! 🤞",
    time: "9:41"
  },
  {
    text: "🎉 Results are in! You scored 8 points this week. You're now 4th in the league! 📈",
    time: "9:42"
  },
];

export const WhatsAppDemo: React.FC = () => {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [typing, setTyping] = useState(false);
  const [delivered, setDelivered] = useState<boolean[]>([false, false]);

  useEffect(() => {
    const sequence = [
      () => { setVisibleMessages(0); setTyping(false); setDelivered([false, false]); },
      () => setTyping(true),
      () => { setTyping(false); setVisibleMessages(1); },
      () => setDelivered([true, false]),
      () => setTyping(true),
      () => { setTyping(false); setVisibleMessages(2); },
      () => setDelivered([true, true]),
      () => { /* pause */ },
    ];

    const timings = [0, 800, 1800, 2200, 2800, 4000, 4400, 7000];
    
    const runSequence = () => {
      sequence.forEach((action, index) => {
        setTimeout(action, timings[index]);
      });
    };

    runSequence();
    const interval = setInterval(runSequence, 8500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col h-full bg-[#0b141a] rounded-[2.25rem] overflow-hidden">
      {/* Status bar for WhatsApp */}
      <div className="h-8 flex items-center justify-between px-6 pt-2 bg-[#0b141a]">
        <span className="text-white/80 text-xs font-medium">9:41</span>
        <div className="flex items-center gap-1">
          <div className="flex gap-0.5">
            <div className="w-1 h-2.5 bg-white/80 rounded-sm" />
            <div className="w-1 h-3 bg-white/80 rounded-sm" />
            <div className="w-1 h-3.5 bg-white/80 rounded-sm" />
            <div className="w-1 h-2 bg-white/40 rounded-sm" />
          </div>
          <div className="w-5 h-2.5 border border-white/80 rounded-sm ml-1">
            <div className="w-3/4 h-full bg-white/80 rounded-sm" />
          </div>
        </div>
      </div>

      {/* WhatsApp Header */}
      <div className="bg-[#1f2c34] px-3 py-2 flex items-center gap-3">
        <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
          <span className="text-white font-bold text-xs">FP</span>
        </div>
        <div className="flex-1">
          <p className="text-white font-medium text-sm">FootyPicks</p>
          <p className="text-green-400 text-[10px]">online</p>
        </div>
      </div>

      {/* Chat Background Pattern */}
      <div 
        className="flex-1 px-3 py-4 overflow-hidden relative"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <div className="space-y-3">
          <AnimatePresence>
            {messages.slice(0, visibleMessages).map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex justify-start"
              >
                <div className="bg-[#1f2c34] rounded-lg rounded-tl-none p-2.5 max-w-[90%] shadow-sm">
                  <p className="text-white text-xs leading-relaxed">{message.text}</p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <span className="text-white/40 text-[9px]">{message.time}</span>
                    {delivered[index] ? (
                      <CheckCheck className="w-3.5 h-3.5 text-blue-400" />
                    ) : (
                      <Check className="w-3.5 h-3.5 text-white/40" />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          <AnimatePresence>
            {typing && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex justify-start"
              >
                <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-4 py-3">
                  <div className="flex gap-1">
                    <motion.div
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: 0 }}
                      className="w-2 h-2 bg-white/60 rounded-full"
                    />
                    <motion.div
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
                      className="w-2 h-2 bg-white/60 rounded-full"
                    />
                    <motion.div
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: 0.4 }}
                      className="w-2 h-2 bg-white/60 rounded-full"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Feature label */}
      <div className="py-3 text-center bg-[#0b141a]">
        <span className="text-white/40 text-xs uppercase tracking-wider">WhatsApp Alerts</span>
      </div>
    </div>
  );
};